// =============================================================================
// Strings added after translations.js
// =============================================================================
//
// Merged over the main table in LanguageContext, so a key here wins over the
// same key there, and every one of them is reached through the ordinary t().
// Callers cannot tell the difference and should not have to.
//
// Why a separate file: translations.js is 80 KB of one flat object, and the
// tooling used on this repository replaces a file whole rather than editing
// it. Adding six lines means rewriting the entire table, where a single typo
// silently breaks every string in the application. A mistake in here reaches
// the keys in here.
//
// The same mechanism corrects a wrong string without touching the large file
// around it, which is what the last section below does.
//
// TONE. Every string in this application is a label in a business tool. It
// states what a thing is or what happened, in as few words as will do it. It
// does not address the reader, does not explain itself, does not encourage,
// and is never written in the first person: the software has no "I". "Nessun
// aggiornamento", not "Non \u00e8 ancora successo niente"; "Salvataggio non
// riuscito", not "Non sono riuscito a salvare".
// =============================================================================

const extraStrings = {
  en: {
    // ----- Shared -----
    "common.close": "Close",
    "common.actionFailed": "Operation failed",

    // ----- Settings -----
    "users.updateFailed": "User not updated",
    "users.createFailed": "User not created",
    "users.deleteFailed": "User not deleted",

    "r2.bucketPlaceholder": "my-bucket",
    "backupR2.bucketPlaceholder": "my-backup-bucket",

    // Overrides translations.js, which still says eight. The server refuses
    // anything under thirty-two.
    "security.newKeyPlaceholder": "Enter the new encryption key (min 32 characters)",

    // The audit log records project actions and had no label for them, so the
    // most common entry in the log wore the raw word in the colour meant for
    // "other".
    "audit.type.project": "Project",

    // Every upload, download and deletion is logged under this type, and it
    // had no label at all - the column printed the bare key. sharepoint keeps
    // its label for the entries written before that section was removed.
    "audit.type.files": "Files",

    // ----- Passwords -----
    "pwd.toggleVisibility": "Show or hide the password",

    // ----- Maintenance -----
    // The bar every account sees while maintenance is on. Its own prefix
    // rather than maintenance.*, which the settings panel already uses: a key
    // here wins over the same key in translations.js, so a name that collides
    // would rewrite the switch's own labels from here without anybody
    // noticing.
    "maintenanceBanner.active": "Maintenance in progress.",
    "maintenanceBanner.readOnly": "Core is available read-only until the work is finished.",
    "maintenanceBanner.manage": "Manage",
    "maintenanceBanner.adminNote": "Administrators can still make changes.",

    // ----- Update notice -----
    // The dialog in components/UpdateNotice.js, shown once per account after
    // an update. These keys describe the current update and are replaced with
    // the next one. They speak to the reader, unlike the rest of this file:
    // the notice is about what changed for them.
    "updateNotice.title": "Core update",
    "updateNotice.planning": "New Planning section",
    "updateNotice.planningBody": "View the shifts and holidays file directly from Core.",
    "updateNotice.calendar": "Home calendar",
    "updateNotice.calendarBody": "Shows your schedule for the month, taken from Planning.",
    "updateNotice.security": "Security updates",
    "updateNotice.ok": "OK",

    // ----- Reminders -----
    // A personal section: every string here is read by the one person whose
    // list it is.
    "reminders.title": "Reminders",
    "reminders.back": "Back to home",
    "reminders.new": "New reminder",
    "reminders.titleLabel": "Title",
    "reminders.titlePlaceholder": "What to remember",
    "reminders.notes": "Notes",
    "reminders.notesPlaceholder": "Optional",
    "reminders.date": "Due date",
    "reminders.datePlaceholder": "No due date",
    "reminders.clearDate": "Remove due date",
    "reminders.time": "Time",
    "reminders.repeat": "Repeat",
    "reminders.repeat.none": "Does not repeat",
    "reminders.repeat.weekly": "Every week",
    "reminders.repeat.monthly": "Every month",
    "reminders.repeat.yearly": "Every year",
    "reminders.needsDate": "Time and repeat need a due date.",
    "reminders.add": "Add",
    "reminders.save": "Save",
    "reminders.cancel": "Cancel",
    "reminders.open": "To do",
    "reminders.done": "Done",
    "reminders.emptyOpen": "No reminders to do",
    "reminders.emptyDone": "No completed reminders",
    "reminders.markDone": "Mark as done",
    "reminders.reopen": "Mark as not done",
    "reminders.edit": "Edit reminder",
    "reminders.delete": "Delete",
    "reminders.deleteTitle": "Delete this reminder?",
    "reminders.deleteText": "The reminder is deleted permanently, with its notes.",
    "reminders.overdue": "Overdue",
    "reminders.today": "Today",
    "reminders.created": "Reminder added",
    "reminders.saved": "Reminder updated",
    "reminders.deleted": "Reminder deleted",
    "reminders.completed": "Reminder done",
    "reminders.nextOn": "Next due {date}",
    "reminders.loadFailed": "Reminders not loaded",

    // ----- Project updates, files and meetings -----
    "file.uploadedBy": "Uploaded by",
    "meeting.deleteTitle": "Delete this meeting?",
    "meeting.timeOrder": "The end time is before the start time",

    // The project file list shows the first few and folds the rest away. The
    // count is appended by the caller, so somebody knows what they are opening
    // before they open it.
    "file.showAll": "Show all files",
    "file.showLess": "Show fewer",

    // Correcting an update. Shown only to whoever wrote it; the mark beside
    // the date is one word because the author is the only person who can have
    // put it there.
    "project.editUpdate": "Edit update",
    "project.edited": "edited",
    "project.updateEdited": "Update edited",
    "project.updateEditFailed": "Update not edited",

    // ----- Trips -----
    // Correcting a recorded trip, which until now meant deleting it and typing
    // it again from memory. The same label serves the pencil on the card and
    // the dialog it opens, because they are the same action.
    "trasferte.editTrip": "Edit trip",
    "trasferte.updated": "Trip updated",

    "trasferte.clients": "Clients",
    "trasferte.tripsIn": "Past trips in",
    "trasferte.noClients": "No clients recorded",
    "trasferte.backToTrips": "All trips",
    "trasferte.searchClient": "Search or create a client",
    "trasferte.createClient": "Create",
    "trasferte.newClient": "New client",
    "trasferte.changeClient": "Change",
    "trasferte.noClient": "No client",
    "trasferte.withoutClient": "Without a client",
    "trasferte.clientOptional": "Optional",
    "trasferte.place": "Place",
    "trasferte.placePlaceholder": "Site or city",
    "trasferte.clientNotes": "Client notes",
    "trasferte.clientNotesPlaceholder": "Contacts, access, opening hours",
    "trasferte.clientCreated": "Client created",
    "trasferte.clientExisted": "Client already exists",
    "trasferte.clientSaved": "Client updated",
    "trasferte.clientSaveFailed": "Client not saved",
    "trasferte.editClient": "Edit client",
    "trasferte.deleteClient": "Delete client",
    "trasferte.deleteClientConfirm": "Delete this client. Its trips must be deleted first.",
    "trasferte.clientDeleted": "Client deleted",
    "trasferte.clientDeleteFailed": "Client not deleted",

    "trasferte.visitsCount": "{count} trips",
    "trasferte.visitsOne": "1 trip",
    "trasferte.visitsNone": "No trips",
    "trasferte.noVisits": "No trips recorded",

    "trasferte.dates": "Dates",
    "trasferte.dateFrom": "From",
    "trasferte.dateTo": "To",
    "trasferte.dateOrder": "The end date is before the start date",

    "trasferte.summary": "Summary",
    "trasferte.hotelsUsed": "Hotels used",
    "trasferte.airportsUsed": "Airports used",

    // Own vehicle: the common case for a client a few hours away, which had no
    // option of its own and was being filed under "other".
    "trasferte.transport.own": "Own vehicle",

    // ----- Corrections to translations.js -----
    "project.noUpdates": "No updates",
    "isotopes.noElements": "No elements",
    "isotopes.noIsotopes": "No isotopes",
    "smtp.notConfigured": "SMTP not configured",
    "decay.noElements": "No elements available. Configure elements and isotopes in Settings.",
    "trasferte.selectPrompt": "Select a country on the map",
    "trasferte.tipsPlaceholder": "Notes",
    "trasferte.noTrips": "No trips recorded",

    // Read twice on the settings page - as the storage section's subtitle and
    // under its entry in the sidebar - and both still called the bucket
    // SharePoint file management. It holds every file in Core.
    "r2.description": "Configure the S3-compatible storage where Core keeps its files.",

    // A lock had no expiry when this was written, so an administrator was the
    // only way out of one. It releases itself now.
    "login.accountLocked": "Account locked after too many failed sign-in attempts. It unlocks automatically fifteen minutes after the last one, or by resetting the password by email.",
  },

  it: {
    // ----- Comuni -----
    "common.close": "Chiudi",
    "common.actionFailed": "Operazione non riuscita",

    // ----- Impostazioni -----
    "users.updateFailed": "Utente non aggiornato",
    "users.createFailed": "Utente non creato",
    "users.deleteFailed": "Utente non eliminato",

    "r2.bucketPlaceholder": "nome-bucket",
    "backupR2.bucketPlaceholder": "nome-bucket-backup",

    "security.newKeyPlaceholder": "Inserisci la nuova chiave (min 32 caratteri)",

    "audit.type.project": "Progetto",

    "audit.type.files": "File",

    // ----- Password -----
    "pwd.toggleVisibility": "Mostra o nascondi la password",

    // ----- Manutenzione -----
    // Gli accenti sono scritti come accenti. Il file usa altrove sequenze di
    // escape, che in una stringa JavaScript funzionano; queste stanno qui in
    // chiaro perche' e' la forma che non si rompe quando il file viene
    // riscritto per intero, ed e' cosi' che sono finiti quattro escape
    // raddoppiati in deps.py.
    "maintenanceBanner.active": "Manutenzione in corso.",
    "maintenanceBanner.readOnly": "È possibile utilizzare Core in sola lettura fino al termine delle operazioni.",
    "maintenanceBanner.manage": "Gestisci",
    "maintenanceBanner.adminNote": "Le modifiche restano consentite agli amministratori.",

    // ----- Avviso delle novità -----
    "updateNotice.title": "Aggiornamento di Core",
    "updateNotice.planning": "Nuova sezione Planning",
    "updateNotice.planningBody": "Visualizza il file turni e ferie direttamente da Core.",
    "updateNotice.calendar": "Calendario della home",
    "updateNotice.calendarBody": "Mostra i tuoi impegni del mese, presi dal Planning.",
    "updateNotice.security": "Aggiornamenti di sicurezza",
    "updateNotice.ok": "Ok",

    // ----- Promemoria -----
    "reminders.title": "Promemoria",
    "reminders.back": "Torna alla home",
    "reminders.new": "Nuovo promemoria",
    "reminders.titleLabel": "Titolo",
    "reminders.titlePlaceholder": "Cosa ricordare",
    "reminders.notes": "Note",
    "reminders.notesPlaceholder": "Facoltative",
    "reminders.date": "Scadenza",
    "reminders.datePlaceholder": "Nessuna scadenza",
    "reminders.clearDate": "Rimuovi scadenza",
    "reminders.time": "Ora",
    "reminders.repeat": "Ripetizione",
    "reminders.repeat.none": "Non si ripete",
    "reminders.repeat.weekly": "Ogni settimana",
    "reminders.repeat.monthly": "Ogni mese",
    "reminders.repeat.yearly": "Ogni anno",
    "reminders.needsDate": "Ora e ripetizione richiedono una scadenza.",
    "reminders.add": "Aggiungi",
    "reminders.save": "Salva",
    "reminders.cancel": "Annulla",
    "reminders.open": "Da fare",
    "reminders.done": "Completati",
    "reminders.emptyOpen": "Nessun promemoria da fare",
    "reminders.emptyDone": "Nessun promemoria completato",
    "reminders.markDone": "Segna come fatto",
    "reminders.reopen": "Rimetti da fare",
    "reminders.edit": "Modifica promemoria",
    "reminders.delete": "Elimina",
    "reminders.deleteTitle": "Eliminare questo promemoria?",
    "reminders.deleteText": "Il promemoria viene eliminato definitivamente, con le sue note.",
    "reminders.overdue": "Scaduto",
    "reminders.today": "Oggi",
    "reminders.created": "Promemoria aggiunto",
    "reminders.saved": "Promemoria aggiornato",
    "reminders.deleted": "Promemoria eliminato",
    "reminders.completed": "Promemoria completato",
    "reminders.nextOn": "Prossima scadenza {date}",
    "reminders.loadFailed": "Promemoria non caricati",

    // ----- Aggiornamenti, file e incontri di progetto -----
    "file.uploadedBy": "Caricato da",
    "meeting.deleteTitle": "Eliminare questo incontro?",
    "meeting.timeOrder": "L'ora di fine precede quella di inizio",

    "file.showAll": "Mostra tutti i file",
    "file.showLess": "Mostra meno",

    "project.editUpdate": "Modifica aggiornamento",
    "project.edited": "modificato",
    "project.updateEdited": "Aggiornamento modificato",
    "project.updateEditFailed": "Aggiornamento non modificato",

    // ----- Trasferte -----
    "trasferte.editTrip": "Modifica trasferta",
    "trasferte.updated": "Trasferta aggiornata",

    "trasferte.clients": "Clienti",
    "trasferte.tripsIn": "Trasferte passate in",
    "trasferte.noClients": "Nessun cliente registrato",
    "trasferte.backToTrips": "Tutte le trasferte",
    "trasferte.searchClient": "Cerca o crea un cliente",
    "trasferte.createClient": "Crea",
    "trasferte.newClient": "Nuovo cliente",
    "trasferte.changeClient": "Cambia",
    "trasferte.noClient": "Senza cliente",
    "trasferte.withoutClient": "Senza cliente",
    "trasferte.clientOptional": "Facoltativo",
    "trasferte.place": "Luogo",
    "trasferte.placePlaceholder": "Sito o citt\u00e0",
    "trasferte.clientNotes": "Note sul cliente",
    "trasferte.clientNotesPlaceholder": "Referenti, accessi, orari",
    "trasferte.clientCreated": "Cliente creato",
    "trasferte.clientExisted": "Cliente gi\u00e0 presente",
    "trasferte.clientSaved": "Cliente aggiornato",
    "trasferte.clientSaveFailed": "Cliente non salvato",
    "trasferte.editClient": "Modifica cliente",
    "trasferte.deleteClient": "Elimina cliente",
    "trasferte.deleteClientConfirm": "Elimina questo cliente. Le trasferte associate vanno eliminate prima.",
    "trasferte.clientDeleted": "Cliente eliminato",
    "trasferte.clientDeleteFailed": "Cliente non eliminato",

    "trasferte.visitsCount": "{count} trasferte",
    "trasferte.visitsOne": "1 trasferta",
    "trasferte.visitsNone": "Nessuna trasferta",
    "trasferte.noVisits": "Nessuna trasferta registrata",

    "trasferte.dates": "Date",
    "trasferte.dateFrom": "Dal",
    "trasferte.dateTo": "Al",
    "trasferte.dateOrder": "La data di fine precede quella di inizio",

    "trasferte.summary": "Riepilogo",
    "trasferte.hotelsUsed": "Hotel utilizzati",
    "trasferte.airportsUsed": "Aeroporti utilizzati",

    "trasferte.transport.own": "Mezzo proprio",

    // ----- Correzioni a translations.js -----
    "project.noUpdates": "Nessun aggiornamento",
    "isotopes.noElements": "Nessun elemento",
    "isotopes.noIsotopes": "Nessun isotopo",
    "smtp.notConfigured": "SMTP non configurato",
    "decay.noElements": "Nessun elemento disponibile. Configurare elementi e isotopi in Impostazioni.",
    "trasferte.selectPrompt": "Seleziona un paese sulla mappa",
    "trasferte.tipsPlaceholder": "Note",
    "trasferte.noTrips": "Nessuna trasferta registrata",

    "r2.description": "Configura lo storage S3-compatibile in cui Core conserva i file.",

    "login.accountLocked": "Account bloccato dopo troppi tentativi di accesso falliti. Si sblocca automaticamente quindici minuti dopo l'ultimo tentativo, oppure reimpostando la password via email.",
  },
};

export default extraStrings;
